import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import { Button, Center, HStack, Spinner } from "@chakra-ui/react";
import { Select } from "../../../../components/Input";
import { fetchData } from "../../../../services/fetch";

export default function TemplateSelectEdit({
  templates,
  templatesIsLoading,
  addQuestion,
  existingQuestions,
}) {
  const [questionOptionsKey, setQuestionOptionsKey] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isSecondSelectEnabled, setIsSecondSelectEnabled] = useState(false);
  const [tempSelectedQuestion, setTempSelectedQuestion] = useState(null);

  const { data: surveyQuestions } = useSWR(
    selectedTemplate ? `/question-template/template/${selectedTemplate}` : null,
    fetchData
  );

  // Memorizar as opções de templates
  const templateOptions = useMemo(
    () =>
      templates?.map((template) => ({
        label: template?.name,
        value: template?.id_survey_template,
      })) || [],
    [templates]
  );

  // Memorizar IDs das questões existentes
  const existingQuestionIds = useMemo(() => {
    return new Set(existingQuestions?.map((q) => q.id_question_template));
  }, [existingQuestions]);

  // Filtrar e mapear as opções de perguntas disponíveis no template selecionado
  const questionOptions = useMemo(() => {
    if (!surveyQuestions?.length) {
      return [];
    }

    const options = [];
    if (existingQuestionIds.size !== surveyQuestions.length) {
      options.push({ label: "Todas as perguntas", value: "all" });
    }

    const individualQuestions = surveyQuestions
      ?.filter((q) => !existingQuestionIds.has(q.id_question_template))
      .map((q) => ({
        label: q.questionTitleTemplate?.[0]?.description,
        value: q.id_question_template,
      }));

    return [...options, ...individualQuestions];
  }, [surveyQuestions, existingQuestionIds]);

  const handleTemplateSelect = (value) => {
    setSelectedTemplate(value);
    setIsSecondSelectEnabled(true);
  };

  const handleQuestionSelect = (value) => {
    setTempSelectedQuestion(value);
  };

  const addSelectedQuestion = () => {
    if (tempSelectedQuestion === "all") {
      const allUnselectedQuestions = surveyQuestions.filter(
        (q) => !existingQuestionIds.has(q.id_question_template)
      );
      allUnselectedQuestions.forEach((q) => addQuestion(q));
    } else {
      const selectedQuestion = surveyQuestions?.find(
        (q) => q?.id_question_template === +tempSelectedQuestion
      );
      if (
        selectedQuestion &&
        !existingQuestionIds.has(selectedQuestion.id_question_template)
      ) {
        addQuestion(selectedQuestion);
      }
    }
    setQuestionOptionsKey((prevKey) => prevKey + 1);
    setTempSelectedQuestion(null);
  };

  if (templatesIsLoading) {
    return (
      <Center py="20px">
        <Spinner />
      </Center>
    );
  }

  return (
    <HStack spacing={6} align="end">
      <Select
        label="Template"
        placeholder="Selecione um template"
        options={templateOptions}
        onChange={(e) => handleTemplateSelect(e.target.value)}
      />
      <Select
        key={questionOptionsKey}
        label="Pergunta"
        placeholder="Selecione uma pergunta"
        options={questionOptions}
        isDisabled={!isSecondSelectEnabled || !surveyQuestions}
        onChange={(e) => handleQuestionSelect(e.target.value)}
      />
      <Button
        variant="primary"
        size="medium"
        w="137px"
        minW="137px"
        isDisabled={!tempSelectedQuestion || tempSelectedQuestion.length === 0}
        onClick={addSelectedQuestion}
      >
        Adicionar
      </Button>
    </HStack>
  );
}
